import React from "react";
import { graphql, Link } from "gatsby";
import { FacebookShareButton, TwitterShareButton, FacebookIcon } from "react-share";
import dayjs from "dayjs";
import Img from "gatsby-image";
import { LayoutInsight as Layout } from "~/templates/LayoutInsight";
import { breakPoints } from "~/utils/variables";
import { colors } from "~/utils/colors";
import { buildInsightArticleBreadcrumbs } from "~/utils/breadcrumb";
import { renderHtmlAst } from "~/molecules/InsightBaseBody";
import type { InsightStructuredDocument } from "~/molecules/InsightRichTextBody";
import { buildInsightTocFromRichText, hasStructuredInsightContent, renderInsightRichText } from "~/molecules/InsightRichTextBody";
import { Author } from "~/molecules/BaseArticleBody";
import { RecommendedContentSidebar } from "~/molecules/RecommendedContentSidebar";
import { TagList } from "~/molecules/TagList";
import { useTaggedContentSections } from "~/hooks/useTaggedContentSections";
import { resolveTagSlugs } from "~/utils/recommendedContent";
import "~/graphql/recommendedFragments";

type InsightArticleNode = GatsbyTypes.ContentfulInsightArticle & {
  structuredIntroduction?: InsightStructuredDocument | null;
  structuredBody?: InsightStructuredDocument | null;
  contentModelVersion?: string | null;
};

type Props = {
  path: string;
  pageContext: {
    contentfulId: string;
    categoryIds?: ReadonlyArray<string>;
    tagSlugs?: ReadonlyArray<string>;
  };
  data: {
    site: GatsbyTypes.Site;
    article: InsightArticleNode;
    recommendCases: GatsbyTypes.ContentfulCaseConnection;
    recommendSeminars: GatsbyTypes.ContentfulEventConnection;
    recommendDownloads: GatsbyTypes.ContentfulDownloadConnection;
    recommendAwards: GatsbyTypes.ContentfulAwardConnection;
    recommendInsights: GatsbyTypes.ContentfulInsightArticleConnection;
  };
};

type TocItem = {
  tagName: string;
  headingText: string;
};

const InsightArticle: React.FC<Props> = ({ path, pageContext, data }) => {
  const { site, article } = data;
  const sharedUrl = article.canonicalUrl || `${site?.siteMetadata?.siteUrl}${encodeURI(path)}`;
  const hasStructuredIntroduction = hasStructuredInsightContent(article.structuredIntroduction);
  const hasStructuredBody = hasStructuredInsightContent(article.structuredBody);
  const tagSlugs = resolveTagSlugs(pageContext.tagSlugs, article.tag);
  const recommendedSections = useTaggedContentSections({
    primaryType: "insight",
    tagSlugs,
    caseEdges: data.recommendCases?.edges || [],
    seminarEdges: data.recommendSeminars?.edges || [],
    downloadEdges: data.recommendDownloads?.edges || [],
    awardEdges: data.recommendAwards?.edges || [],
    insightEdges: data.recommendInsights?.edges || [],
  });
  const showRecommended = recommendedSections.some((section) => section.items.length > 0);

  // H2のみ目次に載せる
  const buildToc = (): TocItem[] => {
    if (hasStructuredBody) {
      return buildInsightTocFromRichText(article.structuredBody);
    }

    const astChildren = (article.body?.childMarkdownRemark?.htmlAst as any)?.children || [];
    const tocItems: TocItem[] = [];

    astChildren.forEach((node: any) => {
      if (node.tagName !== "h2") {
        return;
      }
      const headingText =
        typeof node.children === "string"
          ? node.children
          : Array.isArray(node.children) && node.children[0]?.value
          ? node.children[0].value
          : String(node.children || "");

      tocItems.push({
        tagName: "h2",
        headingText,
      });
    });

    return tocItems;
  };

  const tocItems = buildToc();
  const title = `${article.title}｜Refcome（リフカム）`;

  return (
    <>
      <style jsx>{`
        .articleHeader {
          display: flex;
          flex-direction: column;
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .articleAttribute {
          display: flex;
          align-items: center;
          margin: 20px 0 0;
        }
        .articleAttribute :global(.label) {
          border-radius: 30px;
          font-weight: bold;
          font-size: 12px;
          line-height: 1;
          padding: 8px 12px;
          background: transparent;
          color: #ff7400;
          border: 1px solid #ff7400;
          text-decoration: none;
          display: inline-block;
        }
        .articleAttribute :global(.label:hover) {
          opacity: 0.8;
        }
        .publishedAt {
          color: ${colors.lightText};
          font-size: 13px;
          margin-left: 15px;
        }
        .tagsWrap {
          margin: 12px 0 0;
        }
        .title {
          color: ${colors.defaultText};
          font-weight: 700;
        }
        .articleCover {
          margin: 30px auto 0;
          width: 100%;
          border-radius: 20px;
          overflow: hidden;
        }
        .articleIntroduction {
          margin: 30px auto 0;
        }
        .articleIntroduction :global(p) {
          font-size: 16px;
        }
        .articleIntroduction :global(img:not(.contentThumbImage)) {
          margin: 32px auto;
        }
        .articleIntroduction :global(table) {
          width: 100%;
          border-collapse: collapse;
          margin: 0;
        }
        .articleIntroduction :global(table th),
        .articleIntroduction :global(table td) {
          border: 1px solid ${colors.gray4};
          padding: 12px 16px;
          text-align: left;
        }
        .articleIntroduction :global(table th) {
          background-color: ${colors.gray};
          font-weight: 700;
          font-size: 14px;
        }
        .articleIntroduction :global(ul li),
        .articleIntroduction :global(ol li) {
          margin-top: 10px;
        }
        .articleIntroduction :global(aside.callout) :global(p),
        .articleIntroduction :global(aside.callout) :global(li) {
          font-size: 15px;
          line-height: 1.75;
        }
        .articleIntroduction :global(aside.callout) :global(ul li),
        .articleIntroduction :global(aside.callout) :global(ol li) {
          margin-top: 6px;
        }
        .articleIntroduction :global(aside.contentCard) :global(.contentDescription) {
          font-size: 14px;
        }
        .articleBody {
          display: flex;
          flex-direction: column;
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .articleBody :global(p) {
          font-size: 16px;
        }
        .articleBody :global(img:not(.contentThumbImage)) {
          margin: 32px auto;
        }
        .articleBody :global(table) {
          width: 100%;
          border-collapse: collapse;
          margin: 0;
        }
        .articleBody :global(table th),
        .articleBody :global(table td) {
          border: 1px solid ${colors.gray4};
          padding: 12px 16px;
          text-align: left;
        }
        .articleBody :global(table th) {
          background-color: ${colors.gray};
          font-weight: 700;
          font-size: 14px;
        }
        .articleBody :global(ul li),
        .articleBody :global(ol li) {
          margin-top: 10px;
        }
        .articleBody :global(aside.callout) :global(p),
        .articleBody :global(aside.callout) :global(li) {
          font-size: 15px;
          line-height: 1.75;
        }
        .articleBody :global(aside.callout) :global(ul li),
        .articleBody :global(aside.callout) :global(ol li) {
          margin-top: 6px;
        }
        .articleBody :global(aside.contentCard) :global(.contentDescription) {
          font-size: 14px;
        }
        .tocWrapper {
          box-sizing: border-box;
          margin: 40px auto;
          max-width: 100%;
          padding: 0;
          width: 100%;
        }
        .toc {
          background: ${colors.lightGray};
          border: 1px solid ${colors.gray3};
          border-radius: 12px;
          box-shadow: none;
          box-sizing: border-box;
          overflow: hidden;
          padding: 20px 22px 22px;
          width: 100%;
        }
        .tocTitle {
          border-bottom: 1px solid ${colors.gray3};
          color: ${colors.defaultText};
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.03em;
          line-height: 1.45;
          margin: 0 0 14px;
          padding: 0 0 12px;
        }
        .tocList {
          display: flex;
          flex-direction: column;
          gap: 8px;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .tocItem :global(a) {
          color: ${colors.defaultText};
          font-size: 15px;
          line-height: 1.55;
          text-decoration: none;
          transition: color 0.15s ease-out, opacity 0.15s ease-out;
        }
        .tocItem :global(a:hover) {
          color: ${colors.defaultText};
          opacity: 1;
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-underline-offset: 3px;
        }
        .tocItem {
          margin: 0;
          padding: 0;
        }
        .tocColumn {
          display: none;
        }
        .tocWrapperSide {
          display: none;
        }
        .tocWrapperSide .toc {
          background: transparent;
          border: none;
          border-radius: 0;
          box-shadow: none;
          overflow: visible;
          padding: 0;
        }
        .tocWrapperSide .tocTitle {
          border-bottom: none;
          color: ${colors.defaultText};
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.08em;
          margin: 0 0 14px;
          padding: 0 0 0 14px;
          position: relative;
        }
        .tocWrapperSide .tocTitle::before {
          background: ${colors.orange};
          border-radius: 2px;
          content: "";
          height: 16px;
          left: 0;
          position: absolute;
          top: 2px;
          width: 4px;
        }
        .tocWrapperSide .tocList {
          border-left: 1px solid ${colors.gray3};
          gap: 0;
          padding-left: 0;
        }
        .tocWrapperSide .tocItem {
          margin: 0;
          padding: 0;
        }
        .tocWrapperSide .tocItem :global(a) {
          border-left: 2px solid transparent;
          color: ${colors.lightText};
          display: block;
          font-size: 13px;
          line-height: 1.5;
          margin-left: -1px;
          padding: 9px 0 9px 16px;
          text-decoration: none;
          transition: color 0.15s ease-out, border-color 0.15s ease-out;
        }
        .tocWrapperSide .tocItem :global(a:hover) {
          border-left-color: ${colors.orange};
          color: ${colors.orange};
          opacity: 1;
          text-decoration: none;
        }
        .snsWrapper {
          margin: 40px 0 0;
        }
        .snsShareSide {
          display: none;
        }
        .sns {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 10px;
        }
        /* 両アイコンを同じサイズの枠で統一（FB は react-share の画像・X は自前 SVG で差が出るため） */
        .sns .iconWrap {
          width: 40px;
          height: 40px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .sns .iconWrap :global(> *) {
          width: 40px !important;
          height: 40px !important;
          min-width: 40px !important;
          min-height: 40px !important;
          max-width: 40px !important;
          max-height: 40px !important;
        }
        .sns .iconWrap :global(.facebookShareButton),
        .sns .iconWrap :global(.twitterShareButton) {
          display: flex !important;
          align-items: center;
          justify-content: center;
          outline: none;
          padding: 0;
          border: none;
          margin: 0;
        }
        .sns .iconWrap :global(.facebookShareButton svg) {
          width: 40px !important;
          height: 40px !important;
        }
        .xIcon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #000000;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        .xIcon:hover {
          opacity: 0.8;
        }
        .xIcon svg {
          width: 20px;
          height: 20px;
          fill: #ffffff;
        }
        @media (${breakPoints.sp}) {
          .wrapper {
            margin: 0 auto;
          }
          .sideColumn {
            box-sizing: border-box;
            margin-top: 50px;
            min-width: 0;
            padding: 0 20px;
            width: 100%;
          }
          .title {
            font-size: 22px;
          }
          .tocWrapper {
            margin: 24px auto;
          }
          .toc {
            padding: 18px 16px 20px;
          }
          .tocTitle {
            font-size: 14px;
            letter-spacing: 0.02em;
            margin-bottom: 12px;
            padding-bottom: 10px;
          }
          .tocItem :global(a) {
            font-size: 14px;
          }
          .articleIntroduction :global(p) {
            font-size: 14px;
          }
          .articleBody :global(p) {
            font-size: 14px;
          }
          .articleIntroduction :global(aside.callout) :global(p),
          .articleIntroduction :global(aside.callout) :global(li) {
            font-size: 14px;
            line-height: 1.75;
          }
          .articleBody :global(aside.callout) :global(p),
          .articleBody :global(aside.callout) :global(li) {
            font-size: 14px;
            line-height: 1.75;
          }
          .articleIntroduction :global(aside.contentCard) :global(.contentDescription),
          .articleBody :global(aside.contentCard) :global(.contentDescription) {
            font-size: 13px;
          }
          .articleIntroduction :global(table),
          .articleBody :global(table) {
            font-size: 12px;
          }
          .articleIntroduction :global(table th),
          .articleIntroduction :global(table td),
          .articleBody :global(table th),
          .articleBody :global(table td) {
            padding: 10px 12px;
          }
          .articleIntroduction :global(table th),
          .articleBody :global(table th) {
            font-size: 12px;
          }
          .cvButtonContainerWrapper {
            flex-direction: column;
            gap: 15px;
          }
          .cvButtonContainer :global(a) {
            width: 100%;
          }
        }
        @media (${breakPoints.minPc}) {
          .wrapper {
            max-width: 1340px;
            margin: 0 auto;
            padding: 0 20px;
            display: flex;
            align-items: flex-start;
            gap: 64px;
          }
          .tocColumn {
            display: block;
            width: 240px;
            flex-shrink: 0;
            position: sticky;
            top: 140px;
          }
          .mainColumn {
            flex: 1;
            min-width: 0;
          }
          .sideColumn {
            width: 300px;
            flex-shrink: 0;
          }
          .articleHeader,
          .articleBody {
            max-width: 100%;
            margin: 0;
            padding: 0;
          }
          .title {
            font-size: 28px;
          }
          .tocWrapperInline {
            display: none;
          }
          .tocWrapperSide {
            display: block;
            margin: 0;
            max-width: 100%;
          }
          .snsWrapper {
            display: none;
          }
          .snsShareSide {
            display: flex;
            justify-content: flex-end;
            margin-top: 24px;
          }
        }
      `}</style>
      <Layout
        title={title}
        canonicalUrl={article.canonicalUrl || undefined}
        ogpImageUrl={article?.cover?.fixed ? `https:${article?.cover?.fixed?.src}` : undefined}
        description={data.article.description}
        breadCrumbItems={buildInsightArticleBreadcrumbs(article.title)}
      >
        <div className="wrapper">
          <div className="tocColumn">
            {tocItems.length !== 0 && (
              <div className="tocWrapper tocWrapperSide">
                <nav className="toc" aria-label="この記事の目次">
                  <p className="tocTitle">目次</p>
                  <ol className="tocList">
                    {tocItems.map((item) => (
                      <li key={`tocItemSide_${item.headingText}`} className="tocItem">
                        <Link to={`${path}#${item.headingText}`}>{item.headingText}</Link>
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>
            )}
            <div className="snsShareSide">
              <div className="sns">
                <div className="iconWrap">
                  <FacebookShareButton className="facebookShareButton" url={sharedUrl}>
                    <FacebookIcon size={40} round={true} />
                  </FacebookShareButton>
                </div>
                <div className="iconWrap">
                  <TwitterShareButton className="twitterShareButton" url={sharedUrl} title={article.title}>
                    <div className="xIcon">
                      <svg viewBox="0 0 24 24" aria-label="Xアイコン">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </div>
                  </TwitterShareButton>
                </div>
              </div>
            </div>
          </div>
          <div className="mainColumn">
            <article className="article">
              <div className="articleHeader">
                <h1 className="title">{article.title}</h1>
                <div className="articleAttribute">
                  {article.category?.map((c) => (
                    <Link key={c?.contentful_id} to={`/insight/${c?.slug}`} className="label">
                      {c?.name}
                    </Link>
                  ))}
                  <span className="publishedAt">{dayjs(article.publishedAt).format("YYYY年M月D日")}</span>
                </div>
                {article.tag && article.tag.length > 0 && (
                  <div className="tagsWrap">
                    <TagList tags={article.tag} />
                  </div>
                )}
                <div className="articleCover">
                  <Img fluid={article.cover?.fluid} />
                </div>
                <div className="articleIntroduction">
                  {hasStructuredIntroduction
                    ? renderInsightRichText(article.structuredIntroduction)
                    : renderHtmlAst(article.introduction?.childMarkdownRemark?.htmlAst)}
                </div>
                {tocItems.length !== 0 && (
                  <div className="tocWrapper tocWrapperInline">
                    <nav className="toc" aria-label="この記事の目次">
                      <p className="tocTitle">目次</p>
                      <ol className="tocList">
                        {tocItems.map((item) => (
                          <li key={`tocItemInline_${item.headingText}`} className="tocItem">
                            <Link to={`${path}#${item.headingText}`}>{item.headingText}</Link>
                          </li>
                        ))}
                      </ol>
                    </nav>
                  </div>
                )}
              </div>
              <div className="articleBody">
                {hasStructuredBody ? renderInsightRichText(article.structuredBody) : renderHtmlAst(article.body?.childMarkdownRemark?.htmlAst)}
                {article?.author && (
                  <Author
                    imageUrl={article.author?.facePhotograph?.fluid?.src || ``}
                    name={article.author?.name || ``}
                    company={article.author?.company || ``}
                    position={article.author?.position || ``}
                    description={article.author?.description?.description || ``}
                  />
                )}
                <div className="snsWrapper">
                  <div className="sns">
                    <div className="iconWrap">
                      <FacebookShareButton className="facebookShareButton" url={sharedUrl}>
                        <FacebookIcon size={40} round={true} />
                      </FacebookShareButton>
                    </div>
                    <div className="iconWrap">
                      <TwitterShareButton className="twitterShareButton" url={sharedUrl} title={article.title}>
                        <div className="xIcon">
                          <svg viewBox="0 0 24 24" aria-label="Xアイコン">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        </div>
                      </TwitterShareButton>
                    </div>
                  </div>
                </div>
                <div className="recommendWrapper">
                  <div className="recommend"></div>
                </div>
              </div>
            </article>
          </div>
          <aside className="sideColumn">
            {showRecommended && <RecommendedContentSidebar sections={recommendedSections} />}
          </aside>
        </div>
      </Layout>
    </>
  );
};

export default InsightArticle;

export const insightArticleQuery = graphql`
  query InsightArticle($contentfulId: String) {
    site: site {
      siteMetadata {
        siteUrl
      }
    }
    article: contentfulInsightArticle(contentful_id: { eq: $contentfulId }) {
      structuredBody {
        raw
        references
        plainText
      }
      structuredIntroduction {
        raw
        references
        plainText
      }
      contentModelVersion
      body {
        id
        childMarkdownRemark {
          id
          htmlAst
        }
      }
      introduction {
        id
        childMarkdownRemark {
          id
          htmlAst
        }
      }
      canonicalUrl
      cover {
        fluid(maxWidth: 800, quality: 80) {
          base64
          aspectRatio
          src
          srcSet
          sizes
        }
        fixed(width: 1200) {
          src
        }
      }
      contentful_id
      id
      title
      description
      publishedAt
      category {
        contentful_id
        id
        slug
        name
      }
      tag {
        name
        slug
      }
      author {
        company
        description {
          description
        }
        facePhotograph {
          fluid(maxWidth: 300, quality: 80) {
            src
          }
        }
        name
        position
      }
    }
    recommendCases: allContentfulCase(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: date, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedCaseFields
        }
      }
    }
    recommendSeminars: allContentfulEvent(
      filter: { contentful_id: { ne: $contentfulId } }
      sort: { fields: date, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedSeminarFields
        }
      }
    }
    recommendDownloads: allContentfulDownload(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: createdAt, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedDownloadFields
        }
      }
    }
    recommendAwards: allContentfulAward(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: publishedAt, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedAwardFields
        }
      }
    }
    recommendInsights: allContentfulInsightArticle(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: publishedAt, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedInsightFields
        }
      }
    }
  }
`;
